'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { filterNullable } from '@flipster/contentful-graphql/client';

import { Missions as SharedMissions } from '../shared/missions';
import { MissionGroup, MissionsDataSource, MissionsDataSourceContext } from '../shared/missions/state';
import { luckyDrawBunja, missionsBunja as luckyDrawMissionsBunja } from './state';

interface MissionsTemplateProps {
  /** Event location for segment tracking (default: 'lucky-draw') */
  eventLocation?: string;
  /** Whether to show update time message (default: true) */
  showUpdatedTime?: boolean;
}

/**
 * Lucky Draw Missions component
 * Wraps the shared Missions component with lucky-draw specific data source
 */
export const MissionsTemplate = ({ eventLocation = 'lucky-draw', showUpdatedTime = true }: MissionsTemplateProps) => {
  const { slug } = useBunja(luckyDrawBunja);
  const { missionsLayout, missionsAtom } = useBunja(luckyDrawMissionsBunja);
  const missions = useAtomValue(missionsAtom);

  const dataSource = useMemo<MissionsDataSource | null>(() => {
    if (!slug || !missionsLayout || missionsLayout.length === 0) {
      return null;
    }

    // Transform Contentful layout to MissionGroup[]
    // Lucky draw uses groupName for tab labels
    const groups: MissionGroup[] = filterNullable(missionsLayout).map((item, index) => ({
      id: item.tasksCollection?.items?.[0]?.sys?.id ?? `group-${index}`,
      title: item.groupName ?? item.title,
      icon: item.icon,
      tasks: filterNullable(item.tasksCollection?.items).map((task) => ({
        id: task.id,
        title: task.title,
        description: task.description,
        badge: task.badge,
        button: task.button as { text: string; web: string; app?: string } | null,
      })),
    }));

    return {
      slug,
      eventLocation,
      groups,
      missionsAtom,
      showUpdatedTime,
    };
  }, [slug, missionsLayout, eventLocation, missionsAtom, showUpdatedTime]);

  if (!dataSource || !missions) return null;

  return (
    <MissionsDataSourceContext.Provider value={dataSource}>
      <SharedMissions />
    </MissionsDataSourceContext.Provider>
  );
};

// Re-export for backward compatibility
export { MissionsTemplate as Missions };
